import { Component, h, Prop, Element, Build } from '@stencil/core';
import Splide, { Options } from '@splidejs/splide';
import {
  CmsCategorySliderConfig,
  CmsCategorySliderItem,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-category-slider',
  styleUrl: 'cms-category-slider.scss',
  shadow: true,
})
export class CmsCategorySlider {
  /**
   * Set the Splide configs
   */
  @Prop() config: string;

  @Element() host: HTMLDivElement;

  private options: CmsCategorySliderConfig;
  private items: CmsCategorySliderItem[];
  private splideNode: HTMLElement;
  private splideInner: HTMLElement;
  private splide: Splide;

  private splideConfig = {
    perPage: 4.5,
    perMove: 4,
    focus: 'center',
    trimSpace: true,
    autoHeight: true,
    pagination: false,
    dragAngleThreshold: 50,
    swipeDistanceThreshold: 300,
    flickPower: 500,
    flickVelocityThreshold: 0.5,
    breakpoints: {
      1199: {
        perPage: 3.5,
        perMove: 3,
      },
      767: {
        perPage: 2.5,
        perMove: 2,
      },
      480: {
        perPage: 1.5,
        perMove: 1,
      },
    },
  } as Options;

  componentWillLoad() {
    this.options = JSON.parse(this.config) as CmsCategorySliderConfig;
    this.items = this.options.items.filter(
      (item) => item.name && item.name.trim() !== '',
    );
  }

  componentDidLoad() {
    if (this.options.minSlides > this.items.length) {
      return;
    }
    this.splideNode = this.host.shadowRoot.querySelector('.splide');
    this.splideInner = this.splideNode.querySelector('.splide-inner');
    this.mountSplide();
  }

  private mountSplide() {
    if (!Build.isBrowser || this.options.items.length <= 0) {
      return;
    }
    this.splide = new Splide(this.splideNode, this.splideConfig);
    this.splide.mount();
    this.splide.on('resized', this.updateSize.bind(this));
    this.splide.on('visible', this.updateSize.bind(this));
  }

  private generateUniqueId() {
    return Math.floor(Math.random() * Date.now());
  }

  private updateSize() {
    // Set size to prevent flickering
    this.splideInner.style.height = `${
      this.splideNode.querySelector('.slide').getClientRects()[0].height
    }px`;
  }

  render() {
    if (this.options.minSlides > this.items.length) {
      return (
        <section
          class='invalid-configuration'
          innerHTML={this.options.fallback}
        ></section>
      );
    }
    const sectionStyle = {
      backgroundColor: this.options.background || '#e5e5e5',
    };
    return (
      <section
        class='category-slider-container bg-image-fluid'
        style={sectionStyle}
      >
        {this.options.headline !== undefined ? (
          <cms-headline
            headlineText={this.options.headline}
            headlineTag='h2'
            headlineClass='headline'
            headlinePosition='center'
          ></cms-headline>
        ) : null}
        <div class={'category-slider-inner'}>
          <div class='splide carousel'>
            <div class='splide-inner'>
              <div class='splide__arrows arrows-wrapper'>
                <button
                  type={'button'}
                  class={`splide__arrow splide__arrow--prev arrow-prev`}
                >
                  <cms-icon size='md' icon='arrowLeft'></cms-icon>
                </button>
                <button
                  type={'button'}
                  class={`splide__arrow splide__arrow--next arrow-next`}
                >
                  <cms-icon size='md' icon='arrowRight'></cms-icon>
                </button>
              </div>

              <div class='splide__track track'>
                <ul class='splide__list list'>
                  {this.items.map((item) => (
                    <li
                      key={this.generateUniqueId()}
                      class='splide__slide slide'
                    >
                      <a href={item.url} title={item.titleText} class='cta'>
                        <div class='splide__slide__container slide-container'>
                          <img
                            loading='lazy'
                            width={item.image.width}
                            height={item.image.height}
                            src={item.image.url}
                            alt={item.image.alt}
                            title={item.titleText}
                            class='lazyload'
                          />
                        </div>

                        <div class='button-wrapper'>
                          <button type='button' class='button'>
                            <span>{item.name}</span>
                            <span class='button-svg-wrapper'>
                              <cms-icon
                                colorToken={
                                  '--cms-semanticColors-base-inverted-strong-iconOnBackground'
                                }
                                size='md'
                                icon='arrowRight'
                              ></cms-icon>
                            </span>
                          </button>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}
